import { z } from "zod";
import { StatusTierSchema } from "../dtos/status-tier";
import { baseEntitySchema } from "./base";
import { DealCategory } from "./deal";

// Enum for buy/sell options
export enum ProductType {
  BUY = "buy",
  SELL = "sell",
  BOTH = "both",
}

export enum PaymentOption {
  PAY_IN_ADVANCE = "pay_in_advance",
  PAY_ONCE_BOOKED = "pay_once_booked",
  PAY_AFTER_BOOKING = "pay_after_booking",
}

// Price tier schema for tiered pricing
const PriceTierSchema = z
  .object({
    minRange: z.number().min(0, "Minimum range must be non-negative"),
    maxRange: z.number().min(0, "Maximum range must be non-negative"),
    price: z.number().min(0, "Price must be non-negative"),
  })
  .refine((data) => data.maxRange >= data.minRange, {
    message: "Maximum range must be greater than or equal to minimum range",
    path: ["maxRange"],
  });

// Main product schema
export const productDataSchema = z
  .object({
    id: z.string().optional(),
    logo: z.string().optional(),
    name: z.string().min(1),
    // Short human-friendly code shown in lists (e.g. "AC", "AAdv"). Optional;
    // when absent, callers fall back to initials derived from the name.
    productCode: z.string().optional(),
    inactive: z.boolean().default(false).optional(),

    // Buy or sell configuration
    buyOrSell: z.nativeEnum(ProductType),

    // Category
    category: z.nativeEnum(DealCategory),

    // Overall sell limits
    minimumValueForSell: z.number().min(0),
    maximumValueForSell: z.number().min(0),

    // Overall buy limits
    minimumValueForBuy: z.number().min(0),
    maximumValueForBuy: z.number().min(0),

    // Sell price ranges (array of different pricing tiers)
    sellPrices: z.array(PriceTierSchema),

    // Buy price ranges (array of different pricing tiers)
    buyPrices: z.array(PriceTierSchema),

    // Status tiers
    statusTiers: z.array(StatusTierSchema),

    // Payment options
    paymentOption: z
      .preprocess(
        (val) => (val === null || val === undefined || val === "" ? undefined : val),
        z
          .enum([
            "pay_in_advance",
            "pay_once_booked",
            "pay_after_booking",
          ])
          .optional(),
      ),

    // Optional label overrides for customer-facing forms
    usernameLabelOverride: z.string().optional(),
    emailLabelOverride: z.string().optional(),
    passwordLabelOverride: z.string().optional(),

    // Per-product assignment of keywords from the global pool
    specialAttributeIds: z.array(z.string()).default([]),
  })
  .refine(
    (data) => {
      // Validate sell prices are present when needed
      if (
        (data.buyOrSell === "sell" || data.buyOrSell === "both") &&
        (!data.sellPrices || data.sellPrices.length === 0)
      ) {
        return false;
      }

      // Validate buy prices are present when needed
      if (
        (data.buyOrSell === ProductType.BUY ||
          data.buyOrSell === ProductType.BOTH) &&
        (!data.buyPrices || data.buyPrices.length === 0)
      ) {
        return false;
      }

      return true;
    },
    {
      message: "Price ranges must be provided for the selected buy/sell option",
      path: ["buyOrSell"],
    },
  )
  .refine(
    (data) => {
      // Validate sell min/max values
      return data.maximumValueForSell >= data.minimumValueForSell;
    },
    {
      message:
        "Maximum sell value must be greater than or equal to minimum sell value",
      path: ["maximumValueForSell"],
    },
  )
  .refine(
    (data) => {
      // Validate buy min/max values
      return data.maximumValueForBuy >= data.minimumValueForBuy;
    },
    {
      message:
        "Maximum buy value must be greater than or equal to minimum buy value",
      path: ["maximumValueForBuy"],
    },
  );
export type ProductData = z.infer<typeof productDataSchema>;
export type ProductDataInput = z.input<typeof productDataSchema>;
export const productSchema = baseEntitySchema.and(productDataSchema);
export type Product = z.infer<typeof productSchema>;
export type PriceTier = z.infer<typeof PriceTierSchema>;
